// Interface for cache entries in IndexedDB
interface CacheEntry {
    key: string;
    buffer: ArrayBuffer;
    expiresAt: number; // Timestamp in milliseconds
  }
  
  // Class to manage an IndexedDB-based cache for ArrayBuffers with expiration
  export class ArrayBufferCache {
    private dbPromise: Promise<IDBDatabase> | null = null;
    private readonly dbName = 'ArrayBufferCacheDB';
    private readonly storeName = 'buffers';
  
    // Initialize the IndexedDB database
    private initDB(): Promise<IDBDatabase> {
      if (this.dbPromise) {
        return this.dbPromise;
      }
  
      this.dbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(this.dbName, 1);
  
        request.onerror = () => reject(new Error('Failed to open IndexedDB'));
        request.onsuccess = () => resolve(request.result);
  
        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName, { keyPath: 'key' });
          }
        };
      });
  
      return this.dbPromise;
    }
  
    // Helper to get the current timestamp in milliseconds
    private now(): number {
      return Date.now();
    }
  
    // Delete expired entries from the cache
    private async deleteExpired(): Promise<void> {
      try {
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.openCursor();
  
        return new Promise((resolve, reject) => {
          request.onsuccess = () => {
            const cursor = request.result;
            if (cursor) {
              const entry = cursor.value as CacheEntry;
              if (entry.expiresAt < this.now()) {
                cursor.delete();
              }
              cursor.continue();
            } else {
              resolve();
            }
          };
          request.onerror = () => reject(new Error('Failed to clean up expired entries'));
        });
      } catch (err) {
        console.error('Error cleaning up expired entries:', err);
      }
    }
  
    // Get an ArrayBuffer from the cache
    async get(key: string): Promise<ArrayBuffer | null> {
      try {
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(key);
  
        return new Promise((resolve) => {
          request.onsuccess = async () => {
            const entry = request.result as CacheEntry | undefined;
            if (!entry) {
              resolve(null);
              return;
            }
  
            // Check expiration
            if (entry.expiresAt < this.now()) {
              // Delete expired entry
              const deleteTx = db.transaction([this.storeName], 'readwrite');
              const deleteStore = deleteTx.objectStore(this.storeName);
              deleteStore.delete(key);
              resolve(null);
              return;
            }
  
            resolve(entry.buffer);
          };
          request.onerror = () => resolve(null);
        });
      } catch (err) {
        console.error(`Error reading cache for ${key}:`, err);
        return null;
      }
    }
  
    // Store an ArrayBuffer in the cache with a TTL (in seconds)
    async set(key: string, buffer: ArrayBuffer, ttl: number): Promise<void> {
      try {
        // Optionally clean up expired entries during a set operation
        await this.deleteExpired();
  
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const entry: CacheEntry = {
          key,
          buffer,
          expiresAt: this.now() + ttl * 1000, // Convert TTL (seconds) to milliseconds
        };
        const request = store.put(entry);
  
        return new Promise((resolve, reject) => {
          request.onsuccess = () => resolve();
          request.onerror = () => reject(new Error(`Failed to cache ${key}`));
        });
      } catch (err) {
        console.error(`Error caching ${key}:`, err);
        throw err;
      }
    }
  
    // Delete a specific entry from the cache by key
    async delete(key: string): Promise<void> {
      try {
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(key);
  
        return new Promise((resolve, reject) => {
          request.onsuccess = () => resolve();
          request.onerror = () => reject(new Error(`Failed to delete ${key} from cache`));
        });
      } catch (err) {
        console.error(`Error deleting ${key} from cache:`, err);
        throw err;
      }
    }
  
    // Clear the entire cache
    async clear(): Promise<void> {
      try {
        const db = await this.initDB();
        const transaction = db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.clear();
  
        return new Promise((resolve, reject) => {
          request.onsuccess = () => resolve();
          request.onerror = () => reject(new Error('Failed to clear cache'));
        });
      } catch (err) {
        console.error('Error clearing cache:', err);
        throw err;
      }
    }
  }